import { Link } from "react-router-dom";
import {
  Shield,
  Award,
  Leaf,
  Clock,
  CalendarCheck,
  Users,
  CheckCircle,
} from "lucide-react";
import { Seo, breadcrumbSchema, faqSchema, serviceSchema } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { useSiteContent } from "../lib/useSiteContent";
import { ChecklistSection } from "../components/ChecklistSection";
import { WhyChooseUs } from "../components/WhyChooseUs";
import { ProcessSteps } from "../components/ProcessSteps";
import { FaqAccordion } from "../components/FaqAccordion";
import { RelatedLinks } from "../components/RelatedLinks";
import { ServiceCTA } from "../components/ServiceCTA";

const PATH = "/end-of-tenancy-cleaning-london";

const DEFAULT_FAQS = [
  { question: "What is included in your end of tenancy cleaning service in London?", answer: "Our end of tenancy cleaning in London is a full inventory-grade move-out clean. We deep clean the kitchen including inside cupboards and the oven, descale the bathroom, wipe every surface, hand-clean skirting boards, light switches, door tops and frames, and finish with floors and internal windows. The job is built around the 44-point checklist letting agents use during the checkout report." },
  { question: "Do professional end of tenancy cleaners guarantee my deposit will be returned?", answer: "No cleaning company can sign a deposit decision in your landlord's name, but a credible end of tenancy cleaner can guarantee the cleaning standard. We give you a written 7-day re-clean guarantee: if your landlord or inventory clerk raises any cleaning issue on the checkout report, our team returns and re-cleans the flagged areas at no additional cost." },
  { question: "How long does an end of tenancy clean take in a London flat?", answer: "A studio takes around 3 to 4 hours, a one-bedroom flat 4 to 6 hours, and a three-bedroom house 7 to 9 hours. Larger or heavily soiled properties may need a second day or an extra cleaner. We always confirm the time estimate up front so you can hand the keys back the same day." },
  { question: "Do I need to be there during the end of tenancy clean?", answer: "No. Most London tenants meet us on arrival, hand over the keys and return at the end. We are fully insured, DBS-vetted and used to working with managing agents, building concierge desks and lock-boxes. We can also coordinate directly with your letting agent if that is simpler." },
  { question: "Is end of tenancy cleaning landlord approved across London letting agents?", answer: "Yes. Our cleaning checklist mirrors the AIIC inventory standard that the main London letting agencies — Foxtons, Hamptons, Knight Frank, Dexters, Chestertons and most independents — rely on for their checkout reports. We routinely work alongside their inventory clerks." },
  { question: "Do you provide an inventory check ready clean for landlords?", answer: "Absolutely. Landlords use us between tenancies to prepare a property for the next inventory check-in. The same checklist that satisfies a checkout report works perfectly for a fresh check-in, and we can schedule cleaning to fit your inventory clerk's slot." },
];

const CHECKLIST_GROUPS = [
  {
    title: "Kitchen",
    items: [
      "Oven interior, racks, trays and door glass degreased",
      "Hob, extractor filters and splashbacks descaled",
      "Fridge and freezer defrosted, washed inside and out",
      "Microwave, kettle and toaster cleaned",
      "Inside and outside of all cupboards & drawers",
      "Worktops, sink and taps polished, limescale removed",
      "Floors washed and skirting boards wiped",
    ],
  },
  {
    title: "Bathroom",
    items: [
      "Bath, shower screen and tiles descaled and polished",
      "Toilet sanitised inside, outside and behind",
      "Sink, taps and mirror polished streak-free",
      "Grout and silicone scrubbed where reachable",
      "Extractor fans wiped and dust removed",
      "Cabinets emptied and wiped inside and out",
      "Floors washed and corners detailed",
    ],
  },
  {
    title: "Bedrooms & Living Areas",
    items: [
      "All surfaces dusted including tops of wardrobes & doors",
      "Inside of wardrobes, drawers and shelving wiped",
      "Skirting boards, picture rails and trim hand-cleaned",
      "Light switches, sockets and door handles sanitised",
      "Mirrors and internal glass polished",
      "Carpets vacuumed thoroughly (steam clean as an add-on)",
      "Hard floors washed and edge-detailed",
    ],
  },
  {
    title: "Hallways, Stairs & Extras",
    items: [
      "Stair carpets and treads vacuumed and edged",
      "Banisters and balustrades hand-wiped",
      "Internal doors, frames and tops cleaned",
      "Light fittings and ceiling vents dusted",
      "Inside windows, frames and sills cleaned",
      "Radiators wiped front, top and behind where accessible",
      "Communal entry items (intercom, mat) tidied",
    ],
  },
];

const getWhyChoose = (sc) => [
  { icon: Shield,       title: sc.why1_title || "Fully insured cleaners",   description: sc.why1_desc || "Every team member carries £5M public liability insurance — protecting you, your landlord and the property during the clean." },
  { icon: Award,        title: sc.why2_title || "Re-clean focus",            description: sc.why2_desc || "We work backwards from your checkout report. Every cleaner is trained to clear the same checklist a London inventory clerk will inspect against — backed by our 7-day re-clean guarantee." },
  { icon: Users,        title: sc.why3_title || "Estate-agent standards",    description: sc.why3_desc || "Our specification mirrors the AIIC inventory benchmark used by major London agencies, so the finish reads correctly on the checkout report." },
  { icon: Leaf,         title: sc.why4_title || "Eco-friendly products",     description: sc.why4_desc || "Plant-based, fragrance-free degreasers and descalers — safe around children, pets and sensitive landlords without sacrificing cleaning power." },
  { icon: Clock,        title: sc.why5_title || "Same-day availability",     description: sc.why5_desc || "Last-minute checkout? We routinely fit in same-day and next-day jobs at month-end when most London tenancies expire." },
  { icon: CalendarCheck,title: sc.why6_title || "Weekend bookings",          description: sc.why6_desc || "Saturday, Sunday and public holiday slots are standard — perfect for tenants juggling work, removals and a checkout in the same week." },
];

const getProcess = (sc) => [
  { title: sc.step1_title || "Free online quote",      description: sc.step1_desc || "Send us the property size, postcode and your move-out date. You receive a fixed, all-in quote with no hidden add-ons within a few working hours." },
  { title: sc.step2_title || "We schedule your slot",  description: sc.step2_desc || "Choose a date that aligns with your checkout. We coordinate with your letting agent or inventory clerk so the timing works for everyone." },
  { title: sc.step3_title || "Inventory-grade clean",  description: sc.step3_desc || "A trained team of two to four cleaners arrives with all equipment, dip-tanks for the oven and carpet extractors if booked." },
  { title: sc.step4_title || "Walkthrough & guarantee",description: sc.step4_desc || "We walk through the property with you (or send photos if you have already left) and back the work with a 7-day re-clean guarantee." },
];

const EndOfTenancyCleaningLondon = () => {
  const sc = useSiteContent("eot", {
    eyebrow: "End of Tenancy Cleaning London", h1: "Professional End of Tenancy Cleaning", highlight: "in London",
    subheading: "Inventory-check ready, landlord approved and backed by a 7-day re-clean guarantee. The same checklist your letting agent inspects against — handled in a single visit.",
    overview_eyebrow: "Service Overview", overview_heading: "The London move-out clean built around the inventory checklist",
    overview_p1: "A standard domestic clean and a proper end of tenancy clean are not the same job. Letting agents in London inspect against a specific inventory checklist — the same checklist used by AIIC-trained inventory clerks — and a single missed area on that checklist is often enough to trigger a deposit deduction. Our entire move-out service is built backwards from that document, so the finish your landlord sees on the checkout report is the finish we plan and price for from day one.",
    overview_p2: "We operate across every London postcode from EC and WC in Zone 1 out to TW, KT, SM, CR, BR, DA, RM, IG, EN, HA, UB and the rest of the M25. The team is fully employed, DBS-vetted and trained on the inventory benchmark, so the cleaner who turns up to a luxury serviced apartment in Mayfair works to exactly the same specification as the team handing back a HMO in Walthamstow.",
    overview_p3: "Tenants book us to chase a smooth checkout. Landlords use us between tenancies to keep their portfolio inventory-ready. Letting agents put us on their preferred-supplier list because the work clears the checkout report on the first visit.",
    checklist_eyebrow: "What's Included", checklist_heading: "The complete end of tenancy cleaning checklist", checklist_sub: "Every property gets the same inventory-grade specification. Add-ons such as carpet steam cleaning, exterior windows and balcony cleans are priced separately so you only pay for what your tenancy needs.",
    why_eyebrow: "Why Choose Prime Tenancy", why_heading: "The London end of tenancy cleaner landlords trust", why_sub: "We're not the cheapest tenancy cleaner in London — we are the one most likely to clear your checkout report on the first attempt, backed by our 7-day re-clean guarantee.",
    process_eyebrow: "The Process", process_heading: "From quote to clean in four steps", process_sub: "Most London tenants book us between five and ten days before their checkout — but if you need us tomorrow, we can usually make it work.",
    spec_eyebrow: "Inventory Check Cleaning", spec_heading: "Built for the way London inventory clerks inspect",
    spec_p1: "An inventory clerk does not start with the floor. They start with the tops of doors, the back of the oven and the limescale around the shower seal — the exact spots tenants forget. We train every cleaner to start the visit at the same points the clerk will, which is why our checkout pass rate is significantly higher than a generic domestic team's.",
    spec_p2: "For tenants on a strict checkout-day timeline, we can also coordinate directly with your letting agent's inventory clerk so they walk into a property ready for sign-off rather than a re-clean callback.",
    spec_sidebar: "Common deposit deduction triggers we fix",
    faq_heading: "End of tenancy cleaning London FAQs", faq_sub: "The most common questions tenants and landlords ask about move-out cleans in London.",
    faq1_q: DEFAULT_FAQS[0].question, faq1_a: DEFAULT_FAQS[0].answer,
    faq2_q: DEFAULT_FAQS[1].question, faq2_a: DEFAULT_FAQS[1].answer,
    faq3_q: DEFAULT_FAQS[2].question, faq3_a: DEFAULT_FAQS[2].answer,
    faq4_q: DEFAULT_FAQS[3].question, faq4_a: DEFAULT_FAQS[3].answer,
    faq5_q: DEFAULT_FAQS[4].question, faq5_a: DEFAULT_FAQS[4].answer,
    faq6_q: DEFAULT_FAQS[5].question, faq6_a: DEFAULT_FAQS[5].answer,
    cta_title: "Book your London end of tenancy clean", cta_desc: "Get a fixed-price quote with no hidden fees, or call our London team and we'll talk through your checkout in minutes.",
  });
  const WHY_CHOOSE = getWhyChoose(sc);
  const PROCESS    = getProcess(sc);
  const FAQS = [
    { question: sc.faq1_q, answer: sc.faq1_a }, { question: sc.faq2_q, answer: sc.faq2_a },
    { question: sc.faq3_q, answer: sc.faq3_a }, { question: sc.faq4_q, answer: sc.faq4_a },
    { question: sc.faq5_q, answer: sc.faq5_a }, { question: sc.faq6_q, answer: sc.faq6_a },
  ];
  return (
    <>
      <Seo
        title="End of Tenancy Cleaning London — Landlord Approved Cleaners"
        description="Professional end of tenancy cleaning in London. Inventory-ready, landlord approved cleaners with a 7-day re-clean guarantee. From £99. Same-day & weekend slots."
        ogTitle="End of Tenancy Cleaning London | Inventory Check Cleaning"
        ogDescription="Move-out cleans built around the AIIC inventory checklist. Fully insured London team. 7-day re-clean guarantee."
        path={PATH}
        schema={[
          breadcrumbSchema([
            { name: "Services", path: PATH },
            { name: "End of Tenancy Cleaning London", path: PATH },
          ]),
          faqSchema(FAQS),
          serviceSchema({
            name: "End of Tenancy Cleaning London",
            serviceType: "End of Tenancy Cleaning",
            description:
              "Inventory-grade end of tenancy cleaning across all 32 London boroughs, with a 7-day re-clean guarantee.",
          }),
        ]}
      />

      <PageHero
        eyebrow={sc.eyebrow}
        h1={sc.h1}
        highlight={sc.highlight}
        subheading={sc.subheading}
        breadcrumbs={[
          { name: "Services", path: PATH },
          { name: "End of Tenancy Cleaning London", path: PATH },
        ]}
        image="https://images.unsplash.com/photo-1521012108593-cf7f7c0fbb89?w=1920&q=80"
      />

      {/* Overview */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="max-w-3xl">
            <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.overview_eyebrow}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mt-3 mb-6 leading-tight">{sc.overview_heading}</h2>
            <div className="text-slate-700 text-base md:text-lg leading-relaxed space-y-5">
              <p>{sc.overview_p1}</p>
              <p>{sc.overview_p2}</p>
              <p>
                {sc.overview_p3}{" "}See our full breakdown of{" "}
                <Link to="/prices" className="text-teal font-medium hover:text-navy">end of tenancy cleaning prices in London</Link>{" "}
                or jump straight to the{" "}
                <Link to="/contact#quote" className="text-teal font-medium hover:text-navy">quote form</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      <ChecklistSection
        eyebrow={sc.checklist_eyebrow}
        heading={sc.checklist_heading}
        subheading={sc.checklist_sub}
        groups={CHECKLIST_GROUPS}
        background="slate"
      />

      <WhyChooseUs
        eyebrow={sc.why_eyebrow}
        heading={sc.why_heading}
        subheading={sc.why_sub}
        items={WHY_CHOOSE}
      />

      <ProcessSteps
        eyebrow={sc.process_eyebrow}
        heading={sc.process_heading}
        subheading={sc.process_sub}
        steps={PROCESS}
        background="slate"
      />

      {/* Inventory specifics */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 max-w-6xl mx-auto items-start">
            <div>
              <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.spec_eyebrow}</span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mt-3 mb-5 leading-tight">{sc.spec_heading}</h2>
              <p className="text-slate-700 leading-relaxed mb-5">{sc.spec_p1}</p>
              <p className="text-slate-700 leading-relaxed">
                {sc.spec_p2}{" "}Read our{" "}
                <Link to="/faq" className="text-teal font-medium hover:text-navy">FAQ page</Link>{" "}
                for more on inventory-grade standards.
              </p>
            </div>

            <div className="bg-navy rounded-2xl p-8 md:p-10 text-white">
              <h3 className="font-heading text-2xl font-semibold mb-5">{sc.spec_sidebar}</h3>
              <ul className="space-y-3">
                {[
                  "Grease residue on extractor filters and inside the oven door",
                  "Limescale buildup around taps, shower screens and toilet bases",
                  "Dust on top of wardrobes, doors and curtain poles",
                  "Marks on skirting boards and behind radiators",
                  "Mould spots in the bathroom sealant and silicone joints",
                  "Pet hair embedded in carpet pile and under furniture",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                    <span className="text-slate-200 text-sm leading-relaxed">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <FaqAccordion
        heading={sc.faq_heading}
        subheading={sc.faq_sub}
        faqs={FAQS}
        background="slate"
      />

      <RelatedLinks currentPath={PATH} />

      <ServiceCTA
        title={sc.cta_title}
        description={sc.cta_desc}
        variant="navy"
      />
    </>
  );
};

export default EndOfTenancyCleaningLondon;
